import { useCallback, useEffect, useMemo, useState } from "react";
import { Grid } from "@mui/material";
import { isEqual } from "lodash-es";
import { useDevice } from "../../../../utils/DeviceProvider";
import { calcZones, useSmartalarm } from "./SmartalarmTemplate";
import PartitionList from "./PartitionList";
import { DisplayZoneValue, Partition } from "./types";
import PartitionsAndZones from "./PartitionsAndZones";
import { PZConfiguratorValue } from "./PZConfigurator";

const Partitions = () => {
  const [partitions, setPartitions] = useState<Partition[]>([]);
  const [zones, setZones] = useState<DisplayZoneValue[]>([]);
  const [partitionsAndZonesValue, setPartitionsAndZonesValue] = useState<PZConfiguratorValue | null>(null);

  const {
    device: { config },
  } = useDevice();
  const { value, saved, setValue } = useSmartalarm();

  const onPartitionListChange = useCallback(
    (newValue: Partition[]) => {
      value.plists.partitions = newValue;
      setPartitions((old) => [...old]);
      setValue(value);
    },
    [value, setValue]
  );

  const onPZValueChange = useCallback(
    (newValue: PZConfiguratorValue) => {
      calcZones(value.panels).forEach(({ uuid: zoneId }) => {
        value.plists.zonesPlists[zoneId] = partitions.map((partition) => (newValue[zoneId][partition.uuid] ? "1" : "0")).join("");
      });
      setValue(value);
    },
    [value, partitions, setValue]
  );

  useEffect(() => {
    if (!value) {
      return;
    }
    setPartitions((old) => (isEqual(old, value.plists.partitions) ? old : value.plists.partitions || null));
    const newZones = calcZones(value.panels);
    setZones((old) => (isEqual(old, newZones) ? old : newZones));
    setPartitionsAndZonesValue((old) => {
      const newValue = Object.fromEntries(
        newZones.map((zone) => [
          zone.uuid,
          Object.fromEntries(
            partitions.map((partition, partitionIndex) => [partition.uuid, value.plists.zonesPlists[zone.uuid]?.charAt(partitionIndex) === "1" || false])
          ),
        ])
      );
      return isEqual(old, newValue) ? old : newValue;
    });
  }, [value, partitions]);

  const partitionList = useMemo(
    () => <PartitionList value={config ? partitions : null} saved={saved?.plists.partitions || null} onChange={onPartitionListChange} />,
    [config, partitions, saved?.plists.partitions, onPartitionListChange]
  );

  const partitionsAndZones = useMemo(
    () => (
      <PartitionsAndZones
        partitions={partitions}
        zones={zones}
        value={partitionsAndZonesValue}
        saved={config?.toolboxConfig?.smartalarm.plists.zonesPlists || null}
        onChange={onPZValueChange}
      />
    ),
    [partitions, zones, config, partitionsAndZonesValue, onPZValueChange]
  );

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        {partitionList}
      </Grid>
      <Grid item xs={12}>
        {partitionsAndZones}
      </Grid>
    </Grid>
  );
};

export default Partitions;
